<template>
  <div class="topology-container">
    <div v-if="isOperating" class="loading-overlay">
      <div class="spinner"></div>
    </div>
    <VueFlow v-model:nodes="nodes" v-model:edges="edges" :fit-view-on-init="true" class="vue-flow-theme">
      <Background pattern-color="#aaa" gap="16" />
      <MiniMap
        :node-color="getNodeColor"
        :node-stroke-color="getNodeStrokeColor"
        :node-stroke-width="getNodeStrokeWidth"
        :mask-color="getMaskColor"
      />
      <Controls>
        <button class="control-button" @click="doLayout" :title="t('common.refresh')">
          <i class="fas fa-project-diagram"></i>
        </button>
      </Controls>

      <template #node-vm="{ data }">
        <div class="resource-node vm" :class="[
          (data.vm.status === 'RUNNING' || data.vm.status === 'running') ? 'running' : 'stopped',
          data.vm.isOnPremise ? 'on-premise' : ''
        ]">
          <div class="node-header">
            <i class="node-icon" :class="data.vm.isOnPremise ? 'fas fa-desktop' : 'fas fa-server'"></i>
            <span class="node-status" :class="getVMStatusClass(data.vm)">
              {{ getVMStatus(data.vm) }}
            </span>
            <span v-if="data.vm.isOnPremise" class="on-premise-badge">{{ t('vm.onPremise') }}</span>
          </div>
          <div class="node-name">{{ data.vm.name }}</div>
          <div class="node-zone">{{ data.vm.zone }}</div>
          <div class="node-specs">{{ getMachineType(data.vm) }}</div>
          <div v-if="data.vm.isOnPremise && data.vm.platformName" class="node-platform">
            {{ data.vm.platformName }} {{ data.vm.platformVersion }}
          </div>

          <div v-if="(data.vm.status === 'RUNNING' || data.vm.status === 'running') && !data.vm.isOnPremise" class="vm-usage">
            <div class="usage-item">
              <i class="fas fa-microchip"></i>
              <span class="usage-label">{{ t('vm.cpu') }}:</span>
              <span class="usage-value">{{ getVMUsageInfo(data.vm).cpu }}%</span>
            </div>
            <div class="usage-item">
              <i class="fas fa-memory"></i>
              <span class="usage-label">{{ t('vm.memory') }}:</span>
              <span class="usage-value">{{ getVMUsageInfo(data.vm).memory }}%</span>
            </div>
            <div v-if="getVMUsageInfo(data.vm).gpu !== null" class="usage-item">
              <i class="fas fa-gamepad"></i>
              <span class="usage-label">{{ t('vm.gpu') }}:</span>
              <span class="usage-value">{{ getVMUsageInfo(data.vm).gpu }}%</span>
            </div>
          </div>

          <div v-if="!data.vm.isOnPremise" class="system-disk">
            <div class="disk-item system">
              <i class="fas fa-hdd"></i>
              <span class="disk-name">{{ getSystemDiskName(data.vm) }}</span>
              <span class="disk-size">{{ getSystemDiskSize(data.vm, resources?.disks || []) }} GB</span>
            </div>
          </div>

          <!-- 标签显示 -->
          <div v-if="hasLabels(data.vm)" class="labels-section">
            <div class="labels-display">
              <span
                v-if="data.vm.labels?.owner"
                class="label-tag owner"
                :title="`Owner: ${formatOwnerForDisplay(data.vm.labels.owner)}`"
              >
                <i class="fas fa-user"></i>
                {{ formatOwnerForDisplay(data.vm.labels.owner).split('@')[0] }}
              </span>
              <span
                v-if="data.vm.labels?.team"
                class="label-tag team"
                :title="`Team: ${getTeamDisplayName(data.vm.labels.team)}`"
              >
                <i class="fas fa-users"></i>
                {{ getTeamDisplayName(data.vm.labels.team) }}
              </span>
              <span
                v-if="data.vm.labels?.org"
                class="label-tag org"
                :title="`Org: ${getOrgDisplayName(data.vm.labels.org)}`"
              >
                <i class="fas fa-building"></i>
                {{ getOrgDisplayName(data.vm.labels.org) }}
              </span>
            </div>
          </div>
           <div class="node-actions">
              <!-- Hide start/stop buttons for on-premise instances -->
              <button v-if="(data.vm.status === 'RUNNING' || data.vm.status === 'running') && user && user.role !== 'viewer' && !data.vm.isOnPremise" @click.stop="data.onVmOperation('stop', data.vm.name, data.vm.zone)" class="action-btn stop-btn" :title="t('vm.stopVM')"><i class="fas fa-stop"></i></button>
              <button v-else-if="user && user.role !== 'viewer' && !data.vm.isOnPremise" @click.stop="data.onVmOperation('start', data.vm.name, data.vm.zone)" class="action-btn start-btn" :title="t('vm.startVM')"><i class="fas fa-play"></i></button>
              <!-- 暂时隐藏调整VM按钮 -->
              <!-- <button v-if="user && user.role !== 'viewer'" @click.stop="openResizeModal(data.vm)" class="action-btn resize-btn" :title="t('vm.resizeVM')"><i class="fas fa-expand-arrows-alt"></i></button> -->
              <!-- Hide delete and label buttons for on-premise instances -->
              <button v-if="user && user.role === 'admin' && !data.vm.isOnPremise" @click.stop="data.onVmOperation('delete', data.vm.name, data.vm.zone)" class="action-btn delete-btn" :title="t('vm.deleteVM')"><i class="fas fa-trash"></i></button>
              <button v-if="user && user.role !== 'viewer' && !data.vm.isOnPremise" @click.stop="openLabelModal('vm', data.vm)" class="action-btn label-btn" :title="t('labels.editLabels')"><i class="fas fa-tags"></i></button>
              <InfoPopoverBtn :type="'vm'" :item="data.vm" :currentProjectId="currentProjectId" />
            </div>
          <Handle type="target" :position="Position.Right" />
        </div>
      </template>

      <template #node-disk="{ data }">
        <div class="resource-node disk" :class="{ 'draggable': !data.isAttached }">
            <div v-if="data.isAttached" class="attached-badge">{{ t('disk.attachedDisk') }}</div>
            <div class="node-header">
                <i class="node-icon fas fa-hdd"></i>
                <span class="node-status" :class="getDiskStatusClass(data.disk)">{{ getDiskStatus(data.disk) }}</span>
            </div>
            <div class="node-name">{{ data.disk.name }}</div>
            <div class="node-zone">{{ data.disk.zone }}</div>
            <div class="node-specs">{{ (data.disk.sizeGb || data.disk.size) }} GB</div>

            <!-- 标签显示 -->
            <div v-if="hasLabels(data.disk)" class="labels-section">
              <div class="labels-display">
                <span
                  v-if="data.disk.labels?.owner"
                  class="label-tag owner"
                  :title="`Owner: ${formatOwnerForDisplay(data.disk.labels.owner)}`"
                >
                  <i class="fas fa-user"></i>
                  {{ formatOwnerForDisplay(data.disk.labels.owner).split('@')[0] }}
                </span>
                <span
                  v-if="data.disk.labels?.team"
                  class="label-tag team"
                  :title="`Team: ${getTeamDisplayName(data.disk.labels.team)}`"
                >
                  <i class="fas fa-users"></i>
                  {{ getTeamDisplayName(data.disk.labels.team) }}
                </span>
                <span
                  v-if="data.disk.labels?.org"
                  class="label-tag org"
                  :title="`Org: ${getOrgDisplayName(data.disk.labels.org)}`"
                >
                  <i class="fas fa-building"></i>
                  {{ getOrgDisplayName(data.disk.labels.org) }}
                </span>
              </div>
            </div>

            <div class="node-actions">
                <button v-if="data.isAttached && user && user.role !== 'viewer'" @click.stop="data.onDetachDisk(currentProvider === 'aws' ? data.disk.id : data.disk.name, data.attachedVmName, data.disk.zone)" class="action-btn detach-btn" :title="t('disk.detachDisk')"><i class="fas fa-unlink"></i></button>
                <button v-if="!data.isAttached && user && user.role === 'admin'" @click.stop="data.onDeleteDisk(currentProvider === 'aws' ? data.disk.id : data.disk.name, data.disk.zone)" class="action-btn delete-btn" :title="t('disk.deleteDisk')"><i class="fas fa-trash"></i></button>
                <button v-if="user && user.role !== 'viewer'" @click.stop="openLabelModal('disk', data.disk)" class="action-btn label-btn" :title="t('labels.editLabels')"><i class="fas fa-tags"></i></button>
                <InfoPopoverBtn :type="'disk'" :item="data.disk" :currentProjectId="currentProjectId" />
            </div>

            <Handle v-if="!data.isAttached && user && user.role !== 'viewer'" type="source" :position="Position.Left" />
        </div>
      </template>

      <template #node-region="{ data }">
        <div class="region-node">
          <div class="region-header">
            <i class="fas fa-globe"></i>
            <span class="region-name">{{ data.region === 'unknown' ? t('subregion.unknown') : data.region.toUpperCase() }}</span>
          </div>
        </div>
      </template>

      <template #node-subregion-container="{ data }">
        <div class="subregion-container-wrapper" :style="{ width: data.width + 'px', height: data.height + 'px' }">
          <div class="subregion-container" :style="{
            backgroundColor: data.colors?.background || 'rgba(102, 126, 234, 0.05)',
            borderColor: data.colors?.border || '#667eea'
          }">
            <div class="subregion-header" :style="{
              backgroundColor: data.colors?.header || '#667eea'
            }">
              <i class="fas fa-map-marker-alt"></i>
              <span class="subregion-name">{{ data.subregion === 'unknown' ? t('subregion.unknown') : data.subregion.toUpperCase() }}</span>
            </div>
          </div>
        </div>
      </template>

    </VueFlow>

    <!-- 标签编辑模态框 -->
        <LabelEditModal
      :visible="labelModalVisible"
      :resource-type="labelModalData.resourceType"
      :resource-name="labelModalData.resourceName"
      :zone="labelModalData.zone"
      :current-labels="labelModalData.currentLabels"
      :project-id="currentProjectId"
      @close="closeLabelModal"
      @labels-updated="onLabelsUpdated"
    />

    <!-- VM换spec模态框 -->
    <ResizeVMModal
      v-if="resizeModalVisible"
      :vm="resizeModalData"
      @close="closeResizeModal"
      @resized="onVMResized"
    />
  </div>
</template>

<script>
import { ref, watch, computed } from 'vue';
import { VueFlow, useVueFlow, Position, Handle } from '@vue-flow/core';
import { Background } from '@vue-flow/background';
import { MiniMap } from '@vue-flow/minimap';
import { Controls } from '@vue-flow/controls';
import InfoPopoverBtn from './InfoPopoverBtn.vue';
import LabelEditModal from './LabelEditModal.vue';
import ResizeVMModal from './ResizeVMModal.vue';
import { useI18n } from 'vue-i18n';
import { useResources } from '../composables/useResources.js';
import '@vue-flow/core/dist/style.css';
import '@vue-flow/core/dist/theme-default.css';
import '@vue-flow/controls/dist/style.css';
import '@vue-flow/minimap/dist/style.css';
import { useCloudProvider } from '../composables/useCloudProvider.js';

const { currentProvider } = useCloudProvider();


export default {
  name: 'TopologyView',
  components: { VueFlow, MiniMap, Controls, Background, Handle, InfoPopoverBtn, LabelEditModal, ResizeVMModal },
  props: {
    resources: {
      type: Object,
      required: true
    },
    user: {
      type: Object,
      required: false,
      default: () => ({ role: 'viewer' })
    },
    currentProjectId: {
      type: String,
      required: true
    },
    isOperating: {
      type: Boolean,
      default: false
    }
  },
  emits: ['resource-click', 'attach-disk', 'vm-operation', 'detach-disk', 'delete-disk'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const { getVMUsageBatch } = useResources();
    const nodes = ref([]);
    const edges = ref([]);

    // VM使用率数据存储
    const vmUsageData = ref({});

    // 标签编辑模态框状态
    const labelModalVisible = ref(false);
    const labelModalData = ref({
      resourceType: '',
      resourceName: '',
      zone: '',
      currentLabels: {}
    });

    // VM换spec模态框状态
    const resizeModalVisible = ref(false);
    const resizeModalData = ref(null);

    const { onConnect } = useVueFlow();

    // 添加计算属性来安全地访问 resources
    const resources = computed(() => (props && props.resources) || { vms: [], disks: [] });

    const getNodeColor = (node) => {
      if (node.type === 'vm') {
        return '#28a745'; // Green for VM
      }
      if (node.type === 'disk') {
        return '#ffc107'; // Yellow for disk
      }
      if (node.type === 'region') {
        return '#667eea'; // Blue for region
      }
      if (node.type === 'subregion-container') {
        return '#667eea'; // Blue for subregion container
      }
      return '#e2e8f0'; // Default gray
    };

    const getNodeStrokeColor = (node) => {
      if (node.type === 'subregion-container') {
        return '#4a5568'; // Darker border for subregion containers
      }
      if (node.type === 'vm') {
        return '#1e7e34'; // Darker green border for VMs
      }
      if (node.type === 'disk') {
        return '#e0a800'; // Darker yellow border for disks
      }
      return '#cbd5e0'; // Default border
    };

    const getNodeStrokeWidth = (node) => {
      if (node.type === 'subregion-container') {
        return 2; // Thicker border for subregion containers
      }
      return 1; // Default border width
    };

    const getMaskColor = () => {
      return 'rgba(0, 0, 0, 0.1)'; // Light mask color
    };

    // Generate different colors for subregions
    const getSubregionColors = (subregionIndex) => {
      const colors = [
        { background: 'rgba(102, 126, 234, 0.05)', border: '#667eea', header: '#667eea' }, // Blue
        { background: 'rgba(236, 72, 153, 0.05)', border: '#ec4899', header: '#ec4899' }, // Pink
        { background: 'rgba(34, 197, 94, 0.05)', border: '#22c55e', header: '#22c55e' }, // Green
        { background: 'rgba(245, 158, 11, 0.05)', border: '#f59e0b', header: '#f59e0b' }, // Amber
        { background: 'rgba(168, 85, 247, 0.05)', border: '#a855f7', header: '#a855f7' }, // Purple
        { background: 'rgba(239, 68, 68, 0.05)', border: '#ef4444', header: '#ef4444' }, // Red
        { background: 'rgba(6, 182, 212, 0.05)', border: '#06b6d4', header: '#06b6d4' }, // Cyan
        { background: 'rgba(251, 146, 60, 0.05)', border: '#fb923c', header: '#fb923c' }  // Orange
      ];
      return colors[subregionIndex % colors.length];
    };

    // --- Helper functions from original component ---
    const getVMStatus = (vm) => {
      if (currentProvider.value === 'aws') {
        // AWS EC2 instance statuses
        if (vm.status === 'running') {return t('vm.status.running');}
        if (vm.status === 'stopped') {return t('vm.status.stopped');}
        if (vm.status === 'stopping') {return t('vm.status.stopping');}
        if (vm.status === 'pending') {return t('vm.status.pending');}
        if (vm.status === 'shutting-down') {return t('vm.status.shuttingDown');}
        if (vm.status === 'terminated') {return t('vm.status.terminated');}
        return vm.status || t('common.unknown');
      } else {
        // GCP VM statuses
        if (vm.status === 'RUNNING' || vm.status === 'running') {return t('vm.status.running');}
        if (vm.status === 'STOPPED') {return t('vm.status.stopped');}
        if (vm.status === 'TERMINATED') {return t('vm.status.terminated');}
        if (vm.status === 'SUSPENDED') {return t('vm.status.suspended');}
        if (vm.status === 'SUSPENDING') {return t('vm.status.suspending');}
        if (vm.status === 'STOPPING') {return t('vm.status.stopping');}
        return vm.status || t('common.unknown');
      }
    };

    const getVMStatusClass = (vm) => {
      const status = vm.status?.toLowerCase();
      if (currentProvider.value === 'aws') {
        // AWS EC2 instance status classes
        if (status === 'running') {return 'status-running';}
        if (status === 'stopped') {return 'status-stopped';}
        if (status === 'stopping') {return 'status-stopping';}
        if (status === 'pending') {return 'status-pending';}
        if (status === 'shutting-down') {return 'status-shutting-down';}
        if (status === 'terminated') {return 'status-terminated';}
        return '';
      } else {
        // GCP VM status classes
        if (status === 'running') {return 'status-running';}
        if (status === 'stopped') {return 'status-stopped';}
        if (status === 'terminated') {return 'status-terminated';}
        if (status === 'suspended') {return 'status-suspended';}
        if (status === 'suspending') {return 'status-suspending';}
        if (status === 'stopping') {return 'status-stopping';}
        return '';
      }
    };

    const getDiskStatus = (disk) => {
      if (currentProvider.value === 'aws') {
        // AWS EBS volume statuses
        if (disk.status === 'in-use') {return t('disk.status.attached');}
        if (disk.status === 'available') {return t('disk.status.available');}
        if (disk.status === 'creating') {return t('disk.status.creating');}
        if (disk.status === 'deleting') {return t('disk.status.deleting');}
        if (disk.status === 'deleted') {return t('disk.status.deleted');}
        return disk.status || t('common.unknown');
      } else {
        // GCP disk statuses
        if (disk.status === 'READY') {return t('disk.status.ready');}
        if (disk.status === 'CREATING') {return t('disk.status.creating');}
        if (disk.status === 'RESTORING') {return t('disk.status.restoring');}
        return disk.status || t('common.unknown');
      }
    };

    const getDiskStatusClass = (disk) => {
      const status = disk.status?.toLowerCase();
      if (currentProvider.value === 'aws') {
        // AWS EBS volume status classes
        if (status === 'in-use') {return 'status-attached';}
        if (status === 'available') {return 'status-available';}
        if (status === 'creating') {return 'status-creating';}
        if (status === 'deleting') {return 'status-deleting';}
        if (status === 'deleted') {return 'status-deleted';}
        return '';
      } else {
        // GCP disk status classes
        if (status === 'ready') {return 'status-ready';}
        if (status === 'creating') {return 'status-creating';}
        if (status === 'restoring') {return 'status-restoring';}
        return '';
      }
    };

    const getMachineType = (vm) => {
      if (currentProvider.value === 'aws') {
        return vm.instanceType || 'N/A';
      } else {
        return vm.machineType?.split('/').pop() || 'N/A';
      }
    };

    const getBootDiskAttachment = (vm) => {
      if (currentProvider.value === 'aws') {
        // AWS instances don't have a disks property, we need to find the boot disk from the disks list
        if (!vm || !vm.id) {return null;}
        // For AWS, we'll find the boot disk by checking which disk is attached to this instance as /dev/sda1
        const bootDisk = resources.value?.disks?.find(disk =>
          disk.attachedTo?.some(attachment =>
            attachment.instanceId === vm.id && attachment.device === '/dev/sda1'
          )
        );
        return bootDisk ? { source: bootDisk.name, deviceName: '/dev/sda1' } : null;
      } else {
        // GCP logic
        if (!vm || !vm.disks) {return null;}
        const bootDisk = vm.disks.find(d => d.boot);
        return bootDisk || (vm.disks.length > 0 ? vm.disks[0] : null);
      }
    };

    const getSystemDiskName = (vm) => {
      const bootDisk = getBootDiskAttachment(vm);
      if (!bootDisk) {return t('disk.systemDisk');}
      return bootDisk.deviceName || t('disk.systemDisk');
    };

    const getSystemDiskSize = (vm, allDisks) => {
      const bootDisk = getBootDiskAttachment(vm);
      if (!bootDisk) {return 'N/A';}

      if (currentProvider.value === 'aws') {
        // For AWS, bootDisk.source is the disk name
        const fullDisk = allDisks.find(d => d.name === bootDisk.source);
        if (fullDisk) {return fullDisk.sizeGb || fullDisk.size;}
        return 'N/A';
      } else {
        // GCP logic
        if (bootDisk.initializeParams && bootDisk.initializeParams.diskSizeGb) {
          return bootDisk.initializeParams.diskSizeGb;
        }

        if (bootDisk.source) {
          const diskName = bootDisk.source.split('/').pop();
          const fullDisk = allDisks.find(d => d.name === diskName);
          if (fullDisk) {return fullDisk.sizeGb;}
        }
        return 'N/A';
      }
    };

    // 检查资源是否有标签
    const hasLabels = (resource) => {
      console.log('检查标签:', resource.name, resource.labels);
      return resource.labels && (
        resource.labels.owner ||
        resource.labels.team ||
        resource.labels.org
      );
    };

    // 格式化owner显示
    const formatOwnerForDisplay = (owner) => {
      if (!owner) {return '';}
      let email = owner.replace(/_/g, '.');
      if (!email.endsWith('@robotruck.jp')) {
        email += '@robotruck.jp';
      }
      return email;
    };

    // 获取团队显示名称
    const getTeamDisplayName = (team) => {
      const teamNames = {
        'si-dev': 'SI Dev',
        'algo-dev': 'Algo Dev',
        'infra-dev': 'Infra Dev',
        'hi': 'HI'
      };
      return teamNames[team] || team;
    };

    // 获取组织显示名称
    const getOrgDisplayName = (org) => {
      const orgNames = {
        'eng': 'Engineering',
        'product': 'Product'
      };
      return orgNames[org] || org;
    };

    const getAttachedDisks = (vm, allDisks) => {
      if (currentProvider.value === 'aws') {
        // For AWS, find disks attached to this instance (excluding boot disk)
        if (!vm || !vm.id) {return [];}
        return allDisks.filter(disk =>
          disk.attachedTo?.some(attachment =>
            attachment.instanceId === vm.id && attachment.device !== '/dev/sda1'
          )
        );
      } else {
        // GCP logic
        if (!vm || !vm.disks) {return [];}

        const bootDiskAttachment = getBootDiskAttachment(vm);
        if (!bootDiskAttachment) {
          return [];
        }

        return vm.disks
          .filter(d => d !== bootDiskAttachment) // Exclude the boot disk by object reference
          .map(d => {
            if (!d.source) {return null;}
            const diskName = d.source.split('/').pop();
            return allDisks.find(disk => disk.name === diskName);
          })
          .filter(Boolean);
      }
    };

    const getVMUsageInfo = (vm) => {
      if (vm.status !== 'RUNNING') {
        return { cpu: 0, memory: 0, gpu: null, hasData: false };
      }

      // 从响应式数据中获取使用率
      const usageKey = `${vm.name}-${vm.zone}`;
      return vmUsageData.value[usageKey] || { cpu: 0, memory: 0, gpu: null, hasData: false };
    };

    // 批量获取VM使用率数据
    const fetchVMUsageDataBatch = async (vms) => {
      console.log('TopologyView: 开始批量获取VM使用率数据，VM数量:', vms.length);
      const usageData = await getVMUsageBatch(vms);
      console.log('TopologyView: 批量获取完成，数据:', usageData);

      // 更新响应式数据
      Object.keys(usageData).forEach(key => {
        vmUsageData.value[key] = usageData[key];
      });
      console.log('TopologyView: 响应式数据已更新:', vmUsageData.value);
    };


    onConnect((params) => {
      const sourceNode = nodes.value.find(n => n.id === params.source);
      const targetNode = nodes.value.find(n => n.id === params.target);

      if (sourceNode && targetNode && sourceNode.type === 'disk' && targetNode.type === 'vm') {
        const disk = sourceNode.data.disk;
        const vm = targetNode.data.vm;

        const zone = vm.zone || disk.zone;
        if (!zone) {
          console.error(t('disk.error.missingZone'));
          return;
        }

        // 对于AWS，使用实例ID和卷ID；对于GCP，使用实例名称和磁盘名称
        const vmIdentifier = currentProvider.value === 'aws' ? vm.id : vm.name;
        const diskIdentifier = currentProvider.value === 'aws' ? disk.id : disk.name;
        emit('attach-disk', diskIdentifier, vmIdentifier, zone);
      }
    });

    watch(() => props.resources, async (newResources) => {
      console.log('TopologyView: resources changed', newResources);

      if (!newResources || !newResources.vms || !newResources.disks) {
        console.log('TopologyView: clearing nodes and edges - invalid resources');
        nodes.value = [];
        edges.value = [];
        return;
      }

      // 批量获取所有运行中VM的使用率数据
      await fetchVMUsageDataBatch(newResources.vms);

      console.log('TopologyView: creating nodes for', newResources.vms.length, 'VMs and', newResources.disks.length, 'disks');

      const newNodes = [];
      const newEdges = [];
      const attachedDiskNames = new Set();
      const bootDiskNames = new Set();

      // First, identify all boot disks to exclude them from the unattached list later
      newResources.vms.forEach(vm => {
        const bootDisk = getBootDiskAttachment(vm);
        if (bootDisk && bootDisk.source) {
          const diskName = bootDisk.source.split('/').pop();
          bootDiskNames.add(diskName);
        }
      });

      // Group VMs by subregion (zone)
      const vmsBySubregion = {};
      newResources.vms.forEach(vm => {
        const subregion = vm.zone || 'unknown';
        if (!vmsBySubregion[subregion]) {
          vmsBySubregion[subregion] = [];
        }
        vmsBySubregion[subregion].push(vm);
      });

      // Group disks by subregion (zone)
      const disksBySubregion = {};
      newResources.disks.forEach(disk => {
        if (!attachedDiskNames.has(disk.name) && !bootDiskNames.has(disk.name)) {
          const subregion = disk.zone || 'unknown';
          if (!disksBySubregion[subregion]) {
            disksBySubregion[subregion] = [];
          }
          disksBySubregion[subregion].push(disk);
        }
      });

      let currentY = 50;
      const regionSpacing = 50;
      const vmSpacing = 380;
      const diskSpacing = 200;
      
      // On-premise specific spacing for more compact layout
      const onPremiseVmSpacing = 280; // Increased spacing for on-premise VMs
      const onPremiseDiskSpacing = 180; // Increased spacing for on-premise disks

      // Create nodes for each subregion (sorted alphabetically)
      const sortedSubregions = Object.keys(vmsBySubregion).sort();
      sortedSubregions.forEach((subregion, subregionIndex) => {
        const subregionVMs = vmsBySubregion[subregion] || [];
        const subregionDisks = disksBySubregion[subregion] || [];

        // Calculate subregion container dimensions
        const vmCount = subregionVMs.length;
        const diskCount = subregionDisks.length;
        
        // Use different spacing for on-premise vs cloud resources
        const isOnPremise = subregion === 'on-prem';
        const currentVmSpacing = isOnPremise ? onPremiseVmSpacing : vmSpacing;
        const currentDiskSpacing = isOnPremise ? onPremiseDiskSpacing : diskSpacing;
        
        const containerWidth = isOnPremise ? 1200 : 1600; // Wider container for 3-column layout
        
        // Calculate height based on layout type
        let containerHeight;
        if (isOnPremise) {
          // For on-premise grid layout, calculate based on rows
          const cols = 3;
          const rows = Math.ceil(vmCount / cols);
          containerHeight = Math.max(
            rows * currentVmSpacing + 160,
            diskCount * currentDiskSpacing + 160,
            350
          );
        } else {
          // Regular single-column layout
          containerHeight = Math.max(
            vmCount * currentVmSpacing + 200,
            diskCount * currentDiskSpacing + 200,
            400
          );
        }

        // Get colors for this subregion
        const subregionColors = getSubregionColors(subregionIndex);

        // Create subregion container node
        newNodes.push({
          id: `subregion-container-${subregion}`,
          type: 'subregion-container',
          position: { x: 50, y: currentY },
          style: {
            width: containerWidth,
            height: containerHeight
          },
          data: {
            subregion,
            width: containerWidth,
            height: containerHeight,
            colors: subregionColors
          }
        });

        // Add VMs for this subregion (positioned relative to container)
        subregionVMs.forEach((vm, index) => {
          // Special layout for on-premise: arrange in a more compact grid
          let xPos, yPos;
          if (isOnPremise) {
            // Arrange on-premise VMs in a 3-column grid for better distribution
            const cols = 3;
            const row = Math.floor(index / cols);
            const col = index % cols;
            xPos = 80 + (col * 350); // 350px spacing between columns for 3-column layout
            yPos = 100 + (row * currentVmSpacing);
          } else {
            // Regular single-column layout for cloud resources
            xPos = 150;
            yPos = 120 + (index * currentVmSpacing);
          }
          
          newNodes.push({
            id: `vm-${vm.name}`,
            type: 'vm',
            position: { x: xPos, y: yPos },
            parentNode: `subregion-container-${subregion}`,
            extent: 'parent',
            data: {
              vm,
              onVmOperation: (action, vmName, zone) => {
                // 对于AWS，传递实例ID；对于GCP，传递实例名称
                const identifier = currentProvider.value === 'aws' ? vm.id : vmName;
                emit('vm-operation', action, identifier, zone);
              },
              onResourceClick: (resource, type) => emit('resource-click', resource, type)
            }
          });

          const attachedDisks = getAttachedDisks(vm, newResources.disks) || [];
          attachedDisks.forEach((disk, diskIndex) => {
            if (disk) {
              attachedDiskNames.add(disk.name);
              newNodes.push({
                id: `disk-${disk.name}`,
                type: 'disk',
                position: { x: 600, y: 120 + (index * currentVmSpacing) + (diskIndex * (isOnPremise ? 100 : 180)) },
                parentNode: `subregion-container-${subregion}`,
                extent: 'parent',
                data: {
                  disk,
                  isAttached: true,
                  attachedVmName: vm.name,
                  vmIndex: index, // 添加 VM 索引信息
                  onDetachDisk: (diskName, vmName, zone) => {
                    // 对于AWS，使用实例ID和卷ID；对于GCP，使用实例名称和磁盘名称
                    const vmIdentifier = currentProvider.value === 'aws' ? vm.id : vmName;
                    const diskIdentifier = currentProvider.value === 'aws' ? disk.id : diskName;

                    // 对于AWS，从磁盘的attachedTo信息中获取设备名
                    let device = null;
                    if (currentProvider.value === 'aws' && disk.attachedTo && disk.attachedTo.length > 0) {
                      const attachment = disk.attachedTo.find(att => att.instanceId === vm.id);
                      if (attachment) {
                        device = attachment.device;
                      }
                    }

                    emit('detach-disk', diskIdentifier, vmIdentifier, zone, device);
                  },
                  onResourceClick: (resource, type) => emit('resource-click', resource, type)
                }
              });
              newEdges.push({
                id: `edge-${vm.name}-${disk.name}`,
                source: `vm-${vm.name}`,
                target: `disk-${disk.name}`,
                animated: true,
                style: {
                  strokeWidth: 3,
                  stroke: '#667eea'
                }
              });
            }
          });
        });

        // Add unattached disks for this subregion
        subregionDisks.forEach((disk, index) => {
          // Skip if this disk is already attached to a VM
          if (attachedDiskNames.has(disk.name)) {
            return;
          }

          newNodes.push({
            id: `disk-${disk.name}`,
            type: 'disk',
            position: { x: 1000, y: 120 + (index * currentDiskSpacing) },
            parentNode: `subregion-container-${subregion}`,
            extent: 'parent',
            draggable: true,
            data: {
              disk,
              isAttached: false,
              onResourceClick: (resource, type) => emit('resource-click', resource, type),
              onDeleteDisk: (diskName, zone) => {
                emit('delete-disk', currentProvider.value === 'aws' ? disk.id : diskName, zone);
              }
            }
          });
        });

        // Calculate next subregion position
        currentY += containerHeight + regionSpacing;
      });

      nodes.value = newNodes;
      edges.value = newEdges;

    }, { deep: true, immediate: true });

    // 打开标签编辑模态框
    const openLabelModal = (resourceType, resource) => {
      labelModalData.value = {
        resourceType,
        resourceName: resource.name,
        zone: resource.zone,
        currentLabels: resource.labels || {}
      };
      labelModalVisible.value = true;
    };

    // 关闭标签编辑模态框
    const closeLabelModal = () => {
      labelModalVisible.value = false;
    };

    // 处理标签更新
    const onLabelsUpdated = (newLabels) => {
      // 更新本地资源对象的标签
      const { resourceType, resourceName } = labelModalData.value;
      if (resourceType === 'vm') {
        const vm = resources.value.vms.find(v => v.name === resourceName);
        if (vm) {
          vm.labels = newLabels;
        }
      } else if (resourceType === 'disk') {
        const disk = resources.value.disks.find(d => d.name === resourceName);
        if (disk) {
          disk.labels = newLabels;
        }
      }
    };

    // 打开VM换spec模态框
    const openResizeModal = (vm) => {
      resizeModalData.value = vm;
      resizeModalVisible.value = true;
    };

    // 关闭VM换spec模态框
    const closeResizeModal = () => {
      resizeModalVisible.value = false;
      resizeModalData.value = null;
    };

    // 处理VM换spec完成
    const onVMResized = () => {
      closeResizeModal();
      // 触发资源刷新
      emit('vm-operation', 'refresh', '', '');
    };

    const doLayout = () => {
      // Get all subregion containers
      const subregionContainers = nodes.value.filter(n => n.type === 'subregion-container');

      subregionContainers.forEach(container => {


        // Get VMs and disks in this subregion
        const vmNodes = nodes.value.filter(n =>
          n.type === 'vm' && n.parentNode === container.id
        );
        const attachedDiskNodes = nodes.value.filter(n =>
          n.type === 'disk' && n.parentNode === container.id && n.data.isAttached
        );
        const unattachedDiskNodes = nodes.value.filter(n =>
          n.type === 'disk' && n.parentNode === container.id && !n.data.isAttached
        );

        // Layout VMs based on subregion type
        const isOnPremise = container.data.subregion === 'on-prem';
        
        if (isOnPremise) {
          // 3-column grid layout for on-premise instances
          const cols = 3;
          vmNodes.forEach((node, index) => {
            const row = Math.floor(index / cols);
            const col = index % cols;
            const xPos = 80 + (col * 350); // 350px spacing between columns
            const yPos = 100 + (row * 280); // 280px spacing between rows
            node.position = { x: xPos, y: yPos };
          });
        } else {
          // Single column layout for cloud resources
          vmNodes.forEach((node, index) => {
            node.position = { x: 150, y: 120 + (index * 320) };
          });
        }

        // Layout attached disks next to their VMs
        attachedDiskNodes.forEach((node) => {
          const vmIndex = node.data.vmIndex || 0;
          if (isOnPremise) {
            // For on-premise, position disks relative to their VM's grid position
            const cols = 3;
            const row = Math.floor(vmIndex / cols);
            const col = vmIndex % cols;
            const xPos = 80 + (col * 350) + 200; // Offset to the right of VM
            const yPos = 100 + (row * 280);
            node.position = { x: xPos, y: yPos };
          } else {
            // Regular layout for cloud resources
            node.position = { x: 600, y: 120 + (vmIndex * 320) };
          }
        });

        // Layout unattached disks in the right side of container
        unattachedDiskNodes.forEach((node, index) => {
          if (isOnPremise) {
            // For on-premise, use smaller spacing
            node.position = { x: 1000, y: 120 + (index * 180) };
          } else {
            // Regular layout for cloud resources
            node.position = { x: 1000, y: 120 + (index * 240) };
          }
        });
      });

      // Trigger a refresh on the nodes ref to make sure the view updates
      nodes.value = [...nodes.value];
    };

    return {
      nodes,
      t,
      edges,
      resources,
      Position,
      getVMStatus,
      getVMStatusClass,
      getDiskStatus,
      getDiskStatusClass,
      getMachineType,
      getSystemDiskName,
      getSystemDiskSize,
      getVMUsageInfo,
      fetchVMUsageDataBatch,
      doLayout,
      getNodeColor,
      getNodeStrokeColor,
      getNodeStrokeWidth,
      getMaskColor,
      getSubregionColors,
      getAttachedDisks,
      hasLabels,
      formatOwnerForDisplay,
      getTeamDisplayName,
      getOrgDisplayName,
      labelModalVisible,
      labelModalData,
      openLabelModal,
      closeLabelModal,
      onLabelsUpdated,
      resizeModalVisible,
      resizeModalData,
      openResizeModal,
      closeResizeModal,
      onVMResized
    };
  }
};
</script>

<style scoped>
.topology-container {
  width: 100%;
  height: 100%;
  position: relative;
  min-height: 600px;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.spinner {
  border: 8px solid #f3f3f3;
  border-top: 8px solid #3498db;
  border-radius: 50%;
  width: 60px;
  height: 60px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.vue-flow-theme {
  background-color: #f4f7f6;
}

.resource-node {
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border: 2px solid #e0e0e0;
  width: 300px;
  font-family: 'Inter', sans-serif;
}

.resource-node.vm {
  border-color: #28a745;
  background: #f0fff4;
}

.resource-node.disk {
  border-color: #ffc107;
  background: #fffbe6;
}

.node-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; }
.node-icon { font-size: 18px; }
.node-status { font-size: 11px; padding: 3px 8px; border-radius: 12px; font-weight: 500; }
.node-name { font-weight: 600; font-size: 14px; margin-bottom: 6px; }
.node-zone { font-size: 11px; opacity: 0.8; margin-bottom: 6px; }
.node-specs { font-size: 12px; opacity: 0.9; margin-bottom: 10px; }
.system-disk { margin-bottom: 8px; padding: 8px; background: rgba(0, 0, 0, 0.03); border-radius: 6px; }
.disk-item { display: flex; align-items: center; }
.disk-item i { margin-right: 6px; }
.disk-name { flex: 1; font-weight: 500; }
.disk-size { font-size: 10px; opacity: 0.8; }
.disk-usage { margin-bottom: 10px; }
.usage-bar { width: 100%; height: 6px; background: rgba(0, 0, 0, 0.1); border-radius: 3px; overflow: hidden; margin-bottom: 4px; }
.usage-fill { height: 100%; background: linear-gradient(90deg, #4CAF50, #8BC34A); border-radius: 3px; }
.usage-text { font-size: 10px; opacity: 0.8; }
.node-actions { display: flex; gap: 5px; justify-content: center; margin-top: 10px; }
.action-btn { width: 28px; height: 28px; border: none; border-radius: 50%; cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 12px; transition: all 0.2s ease; color: white; }
.action-btn:hover { transform: scale(1.1); }
.start-btn { background: #4CAF50; }
.stop-btn { background: #f44336; }
.resize-btn { background: #ff9800; }
.info-btn { background: #2196F3; }
.detach-btn { background: #ffc107; }
.delete-btn { background: #757575; }
.label-btn { background: #9c27b0; }
.attached-badge { position: absolute; top: -10px; right: 10px; background: #17a2b8; color: #fff; font-size: 10px; padding: 2px 8px; border-radius: 8px; font-weight: bold; }

/* Status-specific styles */
.status-running { background: rgba(76, 175, 80, 0.2); color: #388e3c; }
.status-stopped, .status-terminated { background: rgba(244, 67, 54, 0.2); color: #d32f2f; }
.status-suspended { background: rgba(255, 152, 0, 0.2); color: #f57c00; }
.status-suspending { background: rgba(255, 152, 0, 0.2); color: #f57c00; }
.status-stopping { background: rgba(255, 152, 0, 0.2); color: #f57c00; }
.status-pending { background: rgba(33, 150, 243, 0.2); color: #1976d2; }
.status-shutting-down { background: rgba(255, 152, 0, 0.2); color: #f57c00; }
.status-ready { background: rgba(76, 175, 80, 0.2); color: #388e3c; }
.status-creating, .status-restoring { background: rgba(255, 152, 0, 0.2); color: #f57c00; }
.status-attached { background: rgba(76, 175, 80, 0.2); color: #388e3c; }
.status-available { background: rgba(33, 150, 243, 0.2); color: #1976d2; }
.status-deleting { background: rgba(244, 67, 54, 0.2); color: #d32f2f; }
.status-deleted { background: rgba(158, 158, 158, 0.2); color: #616161; }

/* On-premise node styles */
.resource-node.vm.on-premise {
  border: 2px solid #9c27b0;
  background: linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%);
}

.resource-node.vm.on-premise.running {
  border-color: #4caf50;
  background: linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%);
}

.resource-node.vm.on-premise.stopped {
  border-color: #f44336;
  background: linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%);
}

.on-premise-badge {
  background: #9c27b0;
  color: white;
  padding: 2px 6px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: bold;
  margin-left: 8px;
}

.node-platform {
  font-size: 11px;
  color: #666;
  margin-top: 2px;
  font-style: italic;
}

.control-button {
  background: #fff;
  border: 1px solid #ccc;
  padding: 8px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}
.control-button:hover {
  background: #f4f4f4;
}

:deep(.vue-flow__minimap) {
  background-color: #fcfcfc;
  border: 1px solid #e2e2e2;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

:deep(.vue-flow__minimap-node) {
  border-radius: 4px;
  border: 1px solid;
}

:deep(.vue-flow__minimap-mask) {
  fill: rgba(0, 0, 0, 0.1);
}

/* Edge styles */
:deep(.vue-flow__edge) {
  stroke-width: 2px;
}

:deep(.vue-flow__edge-path) {
  stroke-width: 2px;
}

:deep(.vue-flow__edge.animated) {
  stroke-width: 3px;
}

.popover-detail-card {
  font-size: 1rem;
  color: #333;
  min-width: 220px;
  max-width: 320px;
  line-height: 1.7;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  box-shadow: 0 4px 16px rgba(80,80,80,0.10);
  padding: 1rem 1.2rem;
}
.popover-title {
  font-weight: bold;
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  color: #396afc;
}

/* 标签样式 */
.labels-section {
  margin: 8px 0;
}

.labels-display {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.label-tag {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 500;
  white-space: nowrap;
}

.label-tag.owner {
  background: #d4edda;
  color: #155724;
}

.label-tag.team {
  background: #d1ecf1;
  color: #0c5460;
}

.label-tag.org {
  background: #fff3cd;
  color: #856404;
}

.label-tag i {
  font-size: 8px;
}

/* Region node styles */
.region-node {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 8px;
  padding: 12px 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border: 2px solid #4a5568;
  min-width: 200px;
  text-align: center;
}

.region-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: white;
  font-weight: 600;
  font-size: 14px;
}

.region-header i {
  font-size: 16px;
}

.region-name {
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Subregion container styles */
.subregion-container-wrapper {
  position: relative;
}

.subregion-container {
  border: 2px solid;
  border-radius: 12px;
  padding: 40px;
  position: relative;
  min-height: 400px;
  box-sizing: border-box;
  width: 100%;
  height: 100%;
}

:deep(.vue-flow__node-subregion-container) {
  background: transparent !important;
  border: none !important;
  padding: 0 !important;
}

.subregion-header {
  position: absolute;
  top: -15px;
  left: 20px;
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-weight: 600;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  z-index: 10;
}

.subregion-header i {
  font-size: 14px;
}

.subregion-name {
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
</style>
